import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  FileArrowDown,
  SealCheck,
} from "@phosphor-icons/react";

const STEP_MOTION = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.25 },
};

function Field({ field, value, onChange }) {
  const testid = `field-${field.name}`;
  if (field.type === "info")
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid={testid}>
        <div className="font-medium text-sm text-primary mb-1">{field.label}</div>
        <p className="text-sm text-muted-foreground">{field.text}</p>
      </div>
    );
  if (field.type === "link")
    return (
      <div className="rounded-lg border border-white/10 bg-zinc-900/40 p-4" data-testid={testid}>
        <div className="font-medium text-sm mb-1">{field.label}</div>
        <p className="text-sm text-muted-foreground mb-3">{field.text}</p>
        <a href={field.url} target="_blank" rel="noopener noreferrer"
          data-testid={`${testid}-anchor`}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[#DBC197] transition-colors">
          {field.cta || "Open link"}
        </a>
      </div>
    );
  if (field.type === "textarea")
    return (
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        className="bg-zinc-900/60 border-white/10 min-h-[100px]"
        data-testid={testid}
        placeholder={field.label}
      />
    );
  if (field.type === "select")
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(field.name, v)}>
        <SelectTrigger className="bg-zinc-900/60 border-white/10" data-testid={testid}>
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${testid}-opt-${o}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  if (field.type === "checkbox-group") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) =>
      onChange(field.name, arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div className="grid sm:grid-cols-2 gap-3" data-testid={testid}>
        {field.options.map((o) => (
          <label
            key={o}
            className="flex items-center gap-3 rounded-lg border border-white/10 bg-zinc-900/40 px-4 py-3 cursor-pointer hover:border-primary/40 transition-colors"
            data-testid={`${testid}-opt-${o}`}
          >
            <Checkbox checked={arr.includes(o)} onCheckedChange={() => toggle(o)} />
            <span className="text-sm">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  return (
    <Input
      type={field.type}
      value={value || ""}
      onChange={(e) => onChange(field.name, e.target.value)}
      className="bg-zinc-900/60 border-white/10"
      data-testid={testid}
      placeholder={field.label}
    />
  );
}

export default function ServiceWizard() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const svc = await api.get(`/services/${serviceId}`);
        setService(svc.data);
        const sub = await api.post("/submissions", { service_id: serviceId });
        setSubmission(sub.data);
        setData(sub.data.data || {});
        setStep(sub.data.status === "submitted" ? svc.data.steps.length : Math.min(sub.data.current_step, svc.data.steps.length - 1));
      } catch (err) {
        toast.error("Something went wrong.");
        navigate("/dashboard");
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  if (!service || !submission)
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="h-96 rounded-xl border border-white/10 bg-zinc-900/40 animate-pulse" />
        </div>
      </div>
    );

  const total = service.steps.length;
  const isReview = step >= total;
  const isSubmitted = submission.status === "submitted";
  const current = service.steps[Math.min(step, total - 1)];

  const requiresPayment = !!service.price;
  const plans = service.plans || [];
  const selectedPlan = plans.find((p) => p.name === data.plan);
  const basePrice = plans.length ? (selectedPlan ? selectedPlan.due_today : 0) : service.price;
  const selectedAddons = (service.addons || []).filter(
    (a) => a.option_field && data[a.option_field] === a.option_value
  );
  const paymentTotal = requiresPayment
    ? basePrice + selectedAddons.reduce((sum, a) => sum + a.price, 0)
    : 0;

  const onChange = (name, value) => {
    setData((d) => ({ ...d, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validateStep = () => {
    const errs = {};
    current.fields.forEach((f) => {
      if (f.required) {
        const v = data[f.name];
        const empty = f.type === "checkbox-group" ? !(Array.isArray(v) && v.length) : !v;
        if (empty) errs[f.name] = "Required";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const persist = async (nextStep, status, method, amount) => {
    setSaving(true);
    try {
      const body = { data, current_step: nextStep, status };
      if (method) body.payment_method = method;
      if (amount != null) body.amount_due = amount;
      const { data: updated } = await api.put(`/submissions/${submission.id}`, body);
      setSubmission(updated);
      return true;
    } catch {
      toast.error("Failed to save. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (isSubmitted) {
      setStep(step + 1);
      return;
    }
    if (!validateStep()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const ns = step + 1;
    const ok = await persist(ns, "in_progress");
    if (ok) setStep(ns);
  };

  const back = () => setStep(Math.max(0, step - 1));

  const submitFinal = async () => {
    const ok = await persist(total, "submitted");
    if (ok) {
      toast.success("Submitted! Our team will be in touch.");
      setStep(total);
    }
  };

  const submitPaid = async () => {
    if (!paymentMethod) {
      toast.error("Please choose a payment method.");
      return;
    }
    const ok = await persist(total, "submitted", paymentMethod, paymentTotal);
    if (ok) {
      toast.success("Request submitted! We'll send your invoice shortly.");
      setStep(total);
    }
  };

  const signContract = async () => {
    if (!consent || !signature.trim()) {
      toast.error("Please agree and type your full legal name to sign.");
      return;
    }
    setSigning(true);
    try {
      const { data: updated } = await api.post(`/submissions/${submission.id}/sign`, { signature });
      setSubmission(updated);
      toast.success("Agreement signed. A copy has been emailed to our team.");
    } catch {
      toast.error("Could not submit your signature. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    let y = 20;
    doc.setFontSize(18);
    doc.text("DAFARGOS HOLDINGS LLC", 20, y);
    y += 8;
    doc.setFontSize(13);
    doc.text(service.name + " — Submission Summary", 20, y);
    y += 10;
    doc.setFontSize(10);
    service.steps.forEach((s) => {
      doc.setFont(undefined, "bold");
      doc.text(s.title, 20, y);
      y += 6;
      doc.setFont(undefined, "normal");
      s.fields.forEach((f) => {
        if (f.type === "info" || f.type === "link") return;
        let v = data[f.name];
        if (Array.isArray(v)) v = v.join(", ");
        const line = `${f.label}: ${v || "-"}`;
        const wrapped = doc.splitTextToSize(line, 170);
        wrapped.forEach((w) => {
          if (y > 280) { doc.addPage(); y = 20; }
          doc.text(w, 24, y);
          y += 5;
        });
      });
      y += 4;
    });
    doc.save(`${service.id}-summary.pdf`);
  };

  const pct = isSubmitted || isReview ? 100 : Math.round((step / total) * 100);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <div className="mb-2 overline text-primary">{service.name}</div>
        <h1 className="font-display text-3xl tracking-tighter mb-6">
          {isReview ? "Review & Submit" : current.title}
        </h1>

        {/* progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span data-testid="wizard-step-label">
              {isReview ? "Final review" : `Step ${step + 1} of ${total}`}
            </span>
            <span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            {...STEP_MOTION}
            className="rounded-xl border border-white/10 bg-zinc-900/50 p-8"
          >
            {isReview ? (
              <div>
                {isSubmitted && (
                  <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4 mb-6" data-testid="submitted-banner">
                    <SealCheck size={24} weight="fill" className="text-primary" />
                    <div>
                      <div className="font-medium">Submission received</div>
                      <div className="text-sm text-muted-foreground">
                        {submission.payment_method
                          ? `Our team is reviewing your information and will send an invoice via ${submission.payment_method} shortly.`
                          : "Our team is reviewing your information."}
                      </div>
                    </div>
                  </div>
                )}
                {isSubmitted && requiresPayment && (
                  <div className="mb-6">
                    {!submission.signed_at ? (
                      <div className="rounded-lg border border-white/10 bg-zinc-900/40 p-5" data-testid="contract-block">
                        <div className="font-display text-sm tracking-tight text-primary mb-2">Service Agreement</div>
                        <div className="text-xs text-muted-foreground max-h-40 overflow-y-auto space-y-2 mb-4 pr-2">
                          <p>This Service Agreement is between DAFARGOS HOLDINGS LLC ("Company") and the client named below for {service.name}. The client authorizes the Company to perform the selected services and agrees to the plan and fees selected during enrollment.</p>
                          <p>Fees are billed via the client's chosen payment method; an invoice and payment link will be sent separately. Monthly plans continue for the selected term. The client consents to electronic communication and to signing this agreement electronically.</p>
                        </div>
                        <label className="flex items-start gap-3 mb-3 cursor-pointer">
                          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="consent-checkbox" className="mt-0.5" />
                          <span className="text-sm text-muted-foreground">I have read and agree to the Service Agreement and consent to sign electronically.</span>
                        </label>
                        <Label className="mb-2 block">Type your full legal name to sign</Label>
                        <Input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="signature-input" placeholder="Full legal name" className="bg-zinc-900/60 border-white/10 mb-3" />
                        <Button onClick={signContract} disabled={signing} data-testid="sign-contract-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197]">
                          {signing ? "Signing..." : "Sign Agreement"}
                        </Button>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-primary/30 bg-primary/5 p-5" data-testid="signed-block">
                        <div className="flex items-center gap-2 mb-3">
                          <SealCheck size={20} weight="fill" className="text-primary" />
                          <span className="font-medium">Agreement signed by {submission.signature}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">A signed copy has been emailed to our team. Here are your next steps:</p>
                        <div className="flex flex-col gap-3">
                          <a href="https://creditheroscore.com/redirect.asp?guid=IK42ZBH6B828&sid=ZSFHSQHP2" target="_blank" rel="noopener noreferrer" data-testid="credit-hero-link" className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[#DBC197]">Sign up for Credit Hero →</a>
                          <a href="https://meetava.app.link/biI52qKRW4b" target="_blank" rel="noopener noreferrer" data-testid="ava-link" className="inline-flex items-center justify-center rounded-md border border-primary/40 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10">Get 35+ points with Ava →</a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-6">
                  {service.steps.map((s) => (
                    <div key={s.title}>
                      <h3 className="font-display text-base tracking-tight mb-3 text-primary">{s.title}</h3>
                      <div className="space-y-2">
                        {s.fields.filter((f) => f.type !== "info" && f.type !== "link").map((f) => {
                          let v = data[f.name];
                          if (Array.isArray(v)) v = v.join(", ");
                          return (
                            <div key={f.name} className="flex justify-between gap-4 text-sm border-b border-white/5 pb-2">
                              <span className="text-muted-foreground">{f.label}</span>
                              <span className="text-right" data-testid={`review-${f.name}`}>{v || "—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {requiresPayment && !isSubmitted && (
                  <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5" data-testid="payment-summary">
                    <div className="font-display text-sm tracking-tight text-primary mb-3">Payment Summary</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{selectedPlan ? selectedPlan.name : service.name}</span>
                        <span>${basePrice.toLocaleString()}</span>
                      </div>
                      {selectedPlan && (
                        <div className="text-xs text-muted-foreground -mt-1">{selectedPlan.terms}</div>
                      )}
                      {selectedAddons.map((a) => (
                        <div key={a.id} className="flex justify-between" data-testid={`summary-addon-${a.id}`}>
                          <span className="text-muted-foreground">
                            {a.label}
                            <span className="text-xs">{a.suffix}</span>
                          </span>
                          <span>+${a.price}</span>
                        </div>
                      ))}
                      <div className="flex justify-between border-t border-white/10 pt-2 mt-2 font-medium">
                        <span>Total due today</span>
                        <span className="text-primary font-display text-base" data-testid="summary-total">
                          ${paymentTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-sm font-medium mb-2">Choose your payment method</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {["Zelle", "Apple Pay", "Chime", "CashApp", "PayPal"].map((m) => (
                          <button
                            type="button"
                            key={m}
                            onClick={() => setPaymentMethod(m)}
                            data-testid={`payment-method-${m.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                              paymentMethod === m
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-white/10 bg-zinc-900/40 hover:border-primary/40"
                            }`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Monthly add-ons renew each month. After you submit, we'll send an invoice and a secure
                      payment link to your chosen method — no payment is taken on this site.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground -mt-2">{current.description}</p>
                {current.fields.map((f) => {
                  if (f.type === "plan-select") {
                    return (
                      <div key={f.name}>
                        <div className="grid gap-4">
                          {(service.plans || []).map((p) => {
                            const selected = data[f.name] === p.name;
                            return (
                              <button
                                type="button"
                                key={p.id}
                                onClick={() => onChange(f.name, p.name)}
                                data-testid={`plan-${p.id}`}
                                className={`text-left rounded-xl border p-5 transition-colors ${
                                  selected
                                    ? "border-primary bg-primary/5"
                                    : "border-white/10 bg-zinc-900/40 hover:border-primary/40"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-display text-base tracking-tight">{p.name}</span>
                                  {selected && <CheckCircle size={18} weight="fill" className="text-primary" />}
                                </div>
                                <div className="text-primary font-medium text-sm mb-1">{p.price_desc}</div>
                                <div className="text-xs text-muted-foreground">{p.terms}</div>
                              </button>
                            );
                          })}
                        </div>
                        {errors[f.name] && (
                          <p className="text-xs text-red-400 mt-2" data-testid={`error-${f.name}`}>{errors[f.name]}</p>
                        )}
                      </div>
                    );
                  }
                  if (f.type === "package-select") {
                    return (
                      <div key={f.name}>
                        <div className="grid gap-4">
                          {(service.packages || []).map((p) => {
                            const selected = data[f.name] === p.name;
                            return (
                              <button
                                type="button"
                                key={p.id}
                                onClick={() => onChange(f.name, p.name)}
                                data-testid={`package-${p.id}`}
                                className={`text-left rounded-xl border p-5 transition-colors ${
                                  selected
                                    ? "border-primary bg-primary/5"
                                    : "border-white/10 bg-zinc-900/40 hover:border-primary/40"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-display text-base tracking-tight">{p.name}</span>
                                  {selected && <CheckCircle size={18} weight="fill" className="text-primary" />}
                                </div>
                                <div className="text-xs text-muted-foreground mb-1">Best for: {p.best_for}</div>
                                <div className="text-primary font-medium text-sm mb-3">{p.price_range}</div>
                                <ul className="space-y-1">
                                  {p.features.map((ft) => (
                                    <li key={ft} className="text-xs text-muted-foreground flex gap-2">
                                      <span className="text-primary mt-0.5">•</span>
                                      <span>{ft}</span>
                                    </li>
                                  ))}
                                </ul>
                              </button>
                            );
                          })}
                        </div>
                        {errors[f.name] && (
                          <p className="text-xs text-red-400 mt-2" data-testid={`error-${f.name}`}>{errors[f.name]}</p>
                        )}
                      </div>
                    );
                  }
                  if (f.type === "info" || f.type === "link") {
                    return (
                      <div key={f.name}>
                        <Field field={f} value={data[f.name]} onChange={onChange} />
                      </div>
                    );
                  }
                  return (
                    <div key={f.name}>
                      <Label className="mb-2 block">
                        {f.label} {f.required && <span className="text-primary">*</span>}
                      </Label>
                      <Field field={f} value={data[f.name]} onChange={onChange} />
                      {f.note && (
                        <p className="text-xs text-muted-foreground mt-1.5">{f.note}</p>
                      )}
                      {errors[f.name] && (
                        <p className="text-xs text-red-400 mt-1" data-testid={`error-${f.name}`}>{errors[f.name]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav buttons */}
        <div className="flex items-center justify-between mt-6 gap-4">
          <Button
            variant="outline"
            onClick={back}
            disabled={step === 0}
            data-testid="wizard-back-button"
            className="border-white/20 bg-white/5 hover:bg-white/10 disabled:opacity-30"
          >
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>

          {isReview ? (
            <div className="flex gap-3">
              {isSubmitted && (
                <Button variant="outline" onClick={downloadPDF} data-testid="download-pdf-button"
                  className="border-white/20 bg-white/5 hover:bg-white/10">
                  <FileArrowDown size={16} className="mr-2" /> Download PDF
                </Button>
              )}
              {!isSubmitted && requiresPayment && (
                <Button onClick={submitPaid} disabled={saving} data-testid="wizard-pay-button"
                  className="bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
                  <CheckCircle size={16} className="mr-2" />
                  {saving ? "Submitting..." : "Submit Request"}
                </Button>
              )}
              {!isSubmitted && !requiresPayment && (
                <Button onClick={submitFinal} disabled={saving} data-testid="wizard-submit-button"
                  className="bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
                  <CheckCircle size={16} className="mr-2" /> {saving ? "Submitting..." : "Submit"}
                </Button>
              )}
            </div>
          ) : (
            <Button onClick={next} disabled={saving} data-testid="wizard-next-button"
              className="bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
              {saving ? "Saving..." : step === total - 1 ? "Review" : "Continue"}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
